// ============================================================
// The cut plan - who leaves, who enters where, who is ringed, and what
// the order becomes. Pure: the hook executes the plan in the order
// `useScenes` documents.
// ============================================================
import type { Combatant } from '../roster/types';
import { outranking, type Token } from '../battlefield/tokens';
import type { Terrain } from '../battlefield/presets';
import { arrivalFor, type DeploymentPoint, type TeamArrivals } from '../battlefield/deployment';
import { teamOf } from '../roster/team';
import type { TurnParticipant, TurnTrackerState } from '../../types';
import { addParticipants, type AdmitOptions } from '../turnTracker';
import { DEFAULT_INITIATIVE_ARRIVAL } from '../initiative';
import type { HiddenMark, ParkedScene, StageDoc, StageState } from './types';
import { onlyThese } from './park';
import { isObject } from '../bodyShape';

export interface CutPlan {
  /** Off the roster: everyone on stage who is not travelling. */
  leaving: Combatant[];
  /** Onto the roster, at a known spot: the target's mark for them, its rev above the mark's and the live token's. */
  entering: Array<{ combatant: Combatant; token: Token }>;
  /** Onto the roster, with no mark on the target: the hook rings them at the marker. */
  ringed: Combatant[];
  /**
   * The same people, grouped by where they arrive (plan scene-editor-library, B): each traveller's team at its own
   * point, other travellers at the main marker; the target's own cast without a mark at the arena's centre (a null
   * marker - 2026-10-28: the deployment area is the travellers' door). One ring each. Empty when nobody is ringed.
   */
  rings: Array<{ marker: DeploymentPoint | null; people: Combatant[] }>;
  /** The target's team arrival points, for the table to take on. */
  arrivals: TeamArrivals;
  /** The roster as it will be: the target's cast, then the travellers, re-numbered. */
  roster: Combatant[];
  terrain: Terrain;
  marker: DeploymentPoint | null;
  tracker: TurnTrackerState;
  /** Who on the new roster is hidden (plan map-display, G1): the hidden travellers and the target's hidden cast. */
  hidden: Record<string, HiddenMark>;
  /**
   * Each traveller's token rev on stage now. Whatever places them next must outrank it (2026-10-17): every screen
   * holds that token, and a lower rev is thrown away as a stale echo (`acceptRemoteToken`) - the GM, writing its own
   * copy, then sees them somewhere else than every player does.
   */
  heldRevs: Record<string, number>;
}

const DEFAULT_ADMIT: AdmitOptions = { arrival: DEFAULT_INITIATIVE_ARRIVAL };

/**
 * Plan the cut from the stage to `target`.
 *
 * Travellers keep their combatant id and their live state - the wounds
 * come with them - and only their POSITION is the scene's: the target's
 * mark if it has ever seen them, otherwise the ring. They join the
 * target's order through the door (`addParticipants`) when that order is
 * live, with the initiative `rollFor` gives them: the dice are the
 * caller's, never this module's.
 *
 * THROWS when a traveller is also parked in the target. That is the
 * invariant, not a case to merge: `refuseCut` is the check the dialog
 * runs first, so reaching the throw is a bug.
 */
export function planCut(
  stage: StageState,
  target: ParkedScene,
  travellers: ReadonlySet<string>,
  rollFor: (combatant: Combatant) => TurnParticipant,
  admit: AdmitOptions = DEFAULT_ADMIT,
): CutPlan {
  const travelling = stage.roster.filter((c) => travellers.has(c.id));
  const leaving = stage.roster.filter((c) => !travellers.has(c.id));

  const parked = new Set(target.cast.map((c) => c.id));
  const clash = travelling.find((c) => parked.has(c.id));
  if (clash) throw new Error(`${clash.name} is on both stages: a person is live on exactly one stage.`);

  const roster = [...target.cast, ...travelling].map((c, i) => ({ ...c, order: i }));
  const heldRevs = Object.fromEntries(travelling.map((c) => [c.id, stage.tokens[c.id]?.rev ?? 0]));
  const { entering, ringed } = arrivals(roster, target.record.marks, heldRevs);

  const hidden = { ...(target.record.hidden ?? {}), ...onlyThese(stage.hidden, travelling) };
  // A hidden traveller takes no seat: the order is shared (ADR 2026-10-07).
  const tracker = seatTravellers(
    target.record.tracker,
    // Never a hidden figure, never an Object (2026-10-26): it travels with its team's arrival group, but takes no seat.
    travelling.filter((c) => !hidden[c.id] && !isObject(c.sheet)),
    rollFor,
    admit,
  );
  const { terrain } = target;
  const { marker } = target.record;
  const teamPoints = target.record.arrivals ?? {};
  const rings = withCastAtCentre(
    groupByArrival(
      ringed.filter((c) => !parked.has(c.id)),
      marker,
      teamPoints,
    ),
    ringed.filter((c) => parked.has(c.id)),
  );
  return { leaving, entering, ringed, rings, arrivals: teamPoints, roster, terrain, marker, tracker, hidden, heldRevs };
}

/** The target's order with the seated travellers let in through the door - when that order is live. */
function seatTravellers(
  parked: TurnTrackerState,
  seated: Combatant[],
  rollFor: (combatant: Combatant) => TurnParticipant,
  admit: AdmitOptions,
): TurnTrackerState {
  return parked.active && seated.length ? addParticipants(parked, seated.map(rollFor), admit) : parked;
}

/**
 * The target's own cast with no mark, at the arena's centre (the null marker) - never the deployment area, which is
 * where travellers come in (the table, 2026-10-28). One ring with any travellers who arrive at the centre too.
 */
function withCastAtCentre(rings: CutPlan['rings'], cast: Combatant[]): CutPlan['rings'] {
  if (!cast.length) return rings;
  const centre = rings.find((r) => r.marker === null);
  if (centre) return rings.map((r) => (r === centre ? { marker: null, people: [...cast, ...r.people] } : r));
  return [{ marker: null, people: cast }, ...rings];
}

/**
 * People grouped by the point they arrive at: their team's, else the main marker - one group per point, in the order
 * first met. The cut rings each group (`CutPlan.rings`); so does the panel, for people added at the table.
 */
export function groupByArrival(
  ringed: Combatant[],
  marker: DeploymentPoint | null,
  arrivals: TeamArrivals | null | undefined,
): CutPlan['rings'] {
  const rings = new Map<DeploymentPoint | null, Combatant[]>();
  for (const c of ringed) {
    const at = arrivalFor(teamOf(c)?.tag, marker, arrivals ?? {});
    rings.set(at, [...(rings.get(at) ?? []), c]);
  }
  return [...rings].map(([at, people]) => ({ marker: at, people }));
}

/** Who walks on to a mark of their own, and who is left for the ring. */
function arrivals(
  roster: Combatant[],
  marks: ParkedScene['record']['marks'],
  heldRevs: CutPlan['heldRevs'],
): { entering: CutPlan['entering']; ringed: Combatant[] } {
  const entering: CutPlan['entering'] = [];
  const ringed: Combatant[] = [];
  for (const c of roster) {
    const mark = marks[c.id];
    const rev = outranking(Math.max(mark?.rev ?? 0, heldRevs[c.id] ?? 0));
    if (mark) entering.push({ combatant: c, token: { ...mark, id: c.id, rev } });
    else ringed.push(c);
  }
  return { entering, ringed };
}

export interface CutRefusal {
  pendingPrompts: number;
  stageSceneId: string | null;
  targetId: string;
  targetCastIds: readonly string[];
  travellers: ReadonlySet<string>;
  /** Combatant id → display name, for the sentence. */
  names?: Record<string, string>;
}

/** The sentence that replaces the CUT button, or null when the cut may go ahead. */
export function refuseCut(args: CutRefusal): string | null {
  if (args.pendingPrompts > 0) return 'A reaction is still pending. Resolve it before the cut.';
  if (args.stageSceneId !== null && args.stageSceneId === args.targetId) return 'That scene is already on stage.';
  const clash = [...args.travellers].find((id) => args.targetCastIds.includes(id));
  if (clash) {
    const name = args.names?.[clash] ?? clash;
    return `${name} is already in that scene. A person is live on exactly one stage.`;
  }
  return null;
}

/** The last cut's travellers, restricted to whoever is on the roster now. Nobody, the first time. */
export function travellerDefaults(stage: StageDoc | null, roster: readonly Combatant[]): Set<string> {
  const here = new Set(roster.map((c) => c.id));
  return new Set((stage?.travellers ?? []).filter((id) => here.has(id)));
}
